import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo } from 'react';
import { SearchContext } from '@context/SearchContextProvider';
import { useRouter, useSearch } from '@tanstack/react-router';
import { useNavigate } from '@utils/AvRouter';

export const useCustomSearchParams = (
  { contextSearchKey = '' }: { contextSearchKey?: string; options?: any } = { contextSearchKey: '' }
): [Record<string, any>, Dispatch<SetStateAction<Record<string, any>>>] => {
  const search = useSearch({ strict: false, structuralSharing: true });
  const navigate = useNavigate();
  const { searchContext, setSearchContext } = useContext(SearchContext);
  const router = useRouter();
  const mountedPath = useMemo(() => window.location.pathname, []);
  useEffect(() => {
    if (mountedPath === window.location.pathname && !router.state.isLoading) {
      if (searchContext[contextSearchKey]) {
        navigate({ search: searchContext[contextSearchKey], replace: true, ignoreBlocker: true });
      }
    }
  }, [window.location.search]);

  const setSearchParam = useCallback(
    filter => {
      if (mountedPath === window.location.pathname) {
        if (contextSearchKey) {
          setSearchContext(prev => {
            const currentParams = prev[contextSearchKey] || search;
            const resolvedFilter = typeof filter === 'function' ? filter(currentParams) : filter;
            return { ...prev, [contextSearchKey]: resolvedFilter };
          });
        }
        navigate({ search: filter, replace: true, ignoreBlocker: true });
      }
    },
    [navigate, search, contextSearchKey]
  );

  const searchParams = useMemo(() => searchContext[contextSearchKey] || search, [search, searchContext[contextSearchKey]]);

  return useMemo(() => [searchParams, setSearchParam], [searchParams]);
};
